import React from "react";

import _ from "lodash";
import moment from "moment";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { ReactComponent as SearchIcon } from "@assets/icons/search.svg";

import { Column, Row } from "@src/components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import SearchInput from "@components/SearchInput";
import { useStores } from "@stores";
import { useContext } from "@screens/TracksScreen/Context";
import { deviceColorAt } from "@screens/TracksScreen/utils";
import { AttachedDevice } from "@services/devicesService";
import * as dot from "@components/Dot";
import { deviceFreshness } from "@src/utils/deviceOnline";
import { useLang } from "@src/hooks/useLang";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";


const Root = styled.div<{hidden?: boolean}>`
  flex-shrink: 0;
  display: ${({hidden}) => hidden ? "none" : "flex"};
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px 0;
  width: 268px;
  max-height: 100vh;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    max-height: unset;
    box-shadow: none;
  }
`;

const StyleHeader = styled.div`
  width: 100%;
  margin-bottom: 12px;
  padding: 0 16px;
  box-sizing: border-box;
`;


const Header: React.FC<{ count?: number }> = () => {
  const ctx = useContext();
  const lang = useLang();

  return (
    <StyleHeader>
      <Column crossAxisSize="max">
        <Text color="#494C5B" type="heading" size={24}>Devices</Text>
        <SizedBox height={12} />
        {ctx.devices.length > 5 ? <SearchInput
          style={{ width: "100%" }}
          value={ctx.query}
          onChange={(e) => ctx.setQuery(e.target.value)}
          placeholder={"Search"}
          icon={<SearchIcon />}
        /> : null}
        {ctx.devices.length === 0 ? (
          <Text style={{ marginTop: 8 }} size={15} color="#899093">
            {lang.tracks.noDevices}
          </Text>
        ) : null}
        </Column>
    </StyleHeader>
  );
}


const StyleRow = styled.div<{ selected?: boolean, highlighted?: boolean }>`
  display: flex;
  padding: 12px 16px;
  box-sizing: border-box;
  border-top: 1px solid rgba(137, 144, 147, 0.3);
  border-right: 2px solid ${({ highlighted }) => (
    highlighted ? "rgb(104, 173, 24)" : "rgb(255, 255, 255)"
  )};
  min-height: 70px;
  background: ${({ highlighted, selected }) => {
    if (highlighted) {
      return selected ? "hsl(88 76% 90% / 1)" : "hsl(88 76% 95% / 1)";
    }
    return selected ? "#f8f8f8" : "#ffffff";
  }};
  transition: 0.4s;
  position: relative;
  cursor: pointer;
  width: 100%;
  :hover {
    background: #f8f8f8;
  }

  & > .more {
    position: absolute;
    inset: 8px 8px auto auto;
    cursor: pointer;
  }
`;

const StyleTitle = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1rem;
  color: rgba(73, 76, 91, 1);
`;

const StyleSubtitle = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1rem;
  color: rgba(73, 76, 91, 1);
  color: rgba(137, 144, 147, 1);
  text-transform: uppercase;
`;

// Status is a dot coloured by how long ago the device last reported
// (deviceFreshness: green < 2 h, yellow < 25 h, gray older, red never).
const Status: React.FC<{ device: AttachedDevice }> = ({ device }) => (
  <dot.FreshnessDot freshness={deviceFreshness(device.getLastSeenAt()?.toDate())} />
);

// LastSeen displays time when device was online for the last time
const LastSeen: React.FC<{ device: AttachedDevice }> = ({ device }) => {
  const last_seen = device.getLastSeenAt();
  if (last_seen === undefined) {
    return null;
  }

  const mseen = moment(last_seen.toDate());
  return <StyleSubtitle>
    <span style={{ color: "rgba(73, 76, 91, 1)" }}>{mseen.fromNow()}</span>
  </StyleSubtitle>;
};

// Checkbox-like swatch shown on the left of each row. When the device is
// selected it fills with the device's plot color so the menu, map and chart
// legend all agree on which color means which device.
const StyleSwatch = styled.div<{ selected: boolean; color: string }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: 10px;
  border-radius: 4px;
  border: 2px solid ${({ selected, color }) => (selected ? color : "rgba(137, 144, 147, 0.5)")};
  background: ${({ selected, color }) => (selected ? color : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 12px;
    height: 12px;
  }
`;

// DeviceRow is an element of the Menu.
const DeviceRow: React.FC<{
  name: string,
  device: AttachedDevice;
  selected: boolean;
  color: string;
} & React.HTMLAttributes<HTMLDivElement>> = ({ device, name, selected, color, ...props }) => {
  return (
    <StyleRow selected={selected} highlighted={selected} {...props}>
      <Row crossAxisSize="max" alignItems="center">
        <StyleSwatch selected={selected} color={color}>
          {selected && (
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          )}
        </StyleSwatch>
        <Column
          justifyContent="space-between"
          crossAxisSize="max"
          mainAxisSize="stretch"
        >
          <StyleTitle><Status device={device} /> {name}</StyleTitle>
          <Row
            mainAxisSize="stretch"
            justifyContent="space-between"
          >
            <StyleSubtitle>{device.getDescription()}</StyleSubtitle>
            <LastSeen device={device} />
          </Row>
        </Column>
      </Row>
    </StyleRow>
  );
};

const StyleBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  justify-content: space-between;
`;

const GroupHeader = styled.div`
  padding: 12px 16px 4px;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(137, 144, 147, 1);
`;

type TDeviceGroup = {
  key: string;
  title: string | null;
  devices: AttachedDevice[];
};

const Menu: React.FC<{
  // Ids of devices currently plotted, in selection order (drives swatch color).
  selectedIds: string[];
  // Toggle a device in/out of the current selection.
  onToggle: (device: AttachedDevice) => void;
  hidden?: boolean;
}> = ({ selectedIds, onToggle, hidden }) => {
  const ctx = useContext();
  const lang = useLang();
  const { farmStore } = useStores();

  const devices = ctx.getDevices();

  // Group the list by farm when the account has more than one farm: devices
  // visible everywhere go under "All farms"; a device pinned to several farms
  // appears under each of them (same selection state).
  const farms = farmStore.farms;
  let groups: TDeviceGroup[];
  if (farms.length > 1) {
    const allFarmsLabel =
      ((lang as any).farms?.allFarms as string) ?? "All farms";
    groups = [];
    const everywhere = devices.filter((d) => d.getFarms().allFarms);
    if (everywhere.length > 0) {
      groups.push({ key: "all", title: allFarmsLabel, devices: everywhere });
    }
    for (const f of farms) {
      const pinned = devices.filter(
        (d) => !d.getFarms().allFarms && d.getFarms().farmIds.includes(f.id)
      );
      if (pinned.length > 0) {
        groups.push({ key: f.id, title: f.name, devices: pinned });
      }
    }
  } else {
    groups = [{ key: "all", title: null, devices }];
  }

  return (
    <Root hidden={hidden}>
      <Column mainAxisSize="stretch" crossAxisSize="max">
        <Header />
        <StyleBody>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              {group.title && <GroupHeader>{group.title}</GroupHeader>}
              {group.devices.map((device) => {
                const name = _.isEmpty(device.getName())
                  ? device.getEsn().toUpperCase()
                  : device.getName();

                const selectionIndex = selectedIds.indexOf(device.getId());
                const selected = selectionIndex >= 0;

                return (<DeviceRow
                  key={`${group.key}-${device.getEsn()}`}
                  name={name}
                  device={device}
                  selected={selected}
                  color={selected ? deviceColorAt(selectionIndex) : ""}
                  onClick={() => onToggle(device)}
                />);
              })}
            </React.Fragment>
          ))}
        </StyleBody>
      </Column>
    </Root>
  );
};

export default observer(Menu);
